import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { Reveal } from '@/components/atoms';
import { SectionHeading } from '@/components/molecules';
import { Section } from '@/components/organisms';
import { introVehicles } from '@/config/introVehicles';
import { type Service } from '@/content/services';

import { SCOPE_TITLE_ID } from '../content';
import { ServiceCostChip, ServiceIncludeList, ServiceProcessTrack } from '../molecules';

const INCLUDES_ID = 'service-includes-heading';
const STEPS_ID = 'service-steps-heading';

/**
 * What the service covers, and the order it runs in.
 *
 * The two halves are the page's most-read block and they belong in one band:
 * "what do I get" and "what happens to me" are the same question asked from
 * either end, and split across two bands the second one gets skipped.
 *
 * They are drawn differently because they are different kinds of list. The
 * inclusions are a ticked set where order carries nothing, so they are a `ul`
 * across two columns. The steps are a sequence, so they are `NextStepList` —
 * an `ol` that draws its numerals from the list's own counter, which means a
 * step inserted into the catalogue later cannot renumber wrongly.
 *
 * The steps are `ServiceProcessTrack` rather than the shared `NextStepList`
 * they used until 2026-10-21. The two draw the same data; what the track adds
 * is a spine through the numerals, and the reason it is worth a component is
 * that a service page's steps are a journey with a length while a form's
 * "what happens next" is not. `NextStepList` is still correct everywhere it
 * is still used.
 *
 * The cost chip closes the band. It is the third place on the page the cost
 * appears — the hub card, the aside above, and here — and that is deliberate
 * rather than redundant: this is the block a reader finishes before deciding
 * whether to get in touch, and "what is included" is a list that invites the
 * question "and what does all that cost" at exactly its last line.
 */
export function ServiceScopeBand({ service }: { service: Service }) {
  return (
    <Section
      tone="muted"
      pattern="diagonals"
      labelledBy={SCOPE_TITLE_ID}
      backdrop={{ picture: introVehicles.hilux.image, placement: 'right' }}
    >
      <SectionHeading
        id={SCOPE_TITLE_ID}
        eyebrow="The detail"
        title="What it covers, and how it runs"
        lede="Anything not on this list is worth asking about before you assume it is included."
      />

      <Grid container spacing={{ xs: 10, md: 12 }}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack spacing={5}>
            <Typography id={INCLUDES_ID} variant="h5" component="h3">
              What is included
            </Typography>
            <ServiceIncludeList items={service.includes} labelledBy={INCLUDES_ID} />

            <Stack
              spacing={3}
              sx={{
                mt: 2,
                p: { xs: 4, md: 5 },
                borderRadius: 3,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontWeight: 700,
                  // `text.disabled` fails AA at this size; quiet, not unreadable.
                  color: 'text.secondary',
                }}
              >
                What it costs
              </Typography>
              <ServiceCostChip cost={service.cost} showNote />
            </Stack>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Reveal direction="right">
            <Stack spacing={5}>
              <Typography id={STEPS_ID} variant="h5" component="h3">
                How it runs
              </Typography>
              <ServiceProcessTrack steps={service.steps} />
            </Stack>
          </Reveal>
        </Grid>
      </Grid>
    </Section>
  );
}
